/**
 * 모달(.modal) · 서랍(.drawer) — 브라우저 기본 <dialog> 를 여닫는다.
 * 서랍은 옆에서 밀려 나오는 모달일 뿐이라 여닫기 규칙이 같다(도움말 서랍 — help-drawer.php).
 *
 * 열기: [data-modal-open="dialog id"] 를 누르면 showModal().
 * 닫기: 안쪽 [data-modal-close] · 바탕 클릭 · Esc(브라우저 기본 동작).
 * 포커스 가두기와 맨 위 레이어는 <dialog> 가, 뒤 화면 스크롤 잠금은 common.css 가 맡는다.
 *
 * 닫아도 입력한 내용은 지우지 않는다. 바탕을 잘못 눌러 닫혀도 다시 열면 그대로 있고,
 * 비우는 일은 저장에 성공한 쪽(각 화면 모듈)이 한다.
 *
 * 저장처럼 오래 걸리는 동안에는 lockModal() 로 잠근다. 잠긴 모달(dialog[aria-busy])은
 * Esc · 바탕 · 닫기 버튼으로 닫히지 않는다. 일이 끝나면 화면 모듈이 직접 close() 한다.
 *
 * 버튼이 옆줄 · 빈 화면처럼 여러 곳에 흩어져 있어서 문서 한 곳에서 위임으로 받는다.
 */

const isBusy = (dialog) => 'true' === dialog?.getAttribute('aria-busy');

/**
 * 모달을 잠그고, 누른 버튼에 진행 중 표시를 한다.
 *
 * - 누른 버튼: 글자를 label 로 바꾸고 aria-busy (도는 원, 흐려지지 않는다 — common.css)
 * - 모달 안 모든 버튼: 비활성 · body(.modal__body): inert (입력도 막는다)
 * - dialog[aria-busy]: 사용자가 닫을 수 없다 (InitModal 이 본다)
 *
 * 돌려주는 함수를 부르면 원래대로 푼다(실패해서 다시 고쳐야 할 때).
 *
 * @param {HTMLDialogElement} dialog
 * @param {HTMLButtonElement | null} button 누른 버튼
 * @param {string} label 진행 중 글자 (예: '저장 중…')
 * @return {() => void}
 */
const lockModal = (dialog, button, label) => {
  const buttons = Array.from(dialog.querySelectorAll('button'));
  // 원래 비활성이던 버튼(되돌릴 사진이 없는 [되돌리기] 등)은 풀 때도 비활성으로 둔다
  const wasDisabled = buttons.map((item) => item.disabled);
  const bodies = Array.from(dialog.querySelectorAll('.modal__body'));
  const original = button ? button.textContent : '';

  dialog.setAttribute('aria-busy', 'true');
  buttons.forEach((item) => {
    item.disabled = true;
  });
  bodies.forEach((body) => {
    body.inert = true;
  });

  if (button) {
    button.textContent = label;
    button.setAttribute('aria-busy', 'true');
  }

  return () => {
    dialog.removeAttribute('aria-busy');
    buttons.forEach((item, index) => {
      item.disabled = wasDisabled[index];
    });
    bodies.forEach((body) => {
      body.inert = false;
    });

    if (button) {
      button.textContent = original;
      button.removeAttribute('aria-busy');
    }
  };
};

const isOverlay = (element) =>
  element instanceof HTMLDialogElement &&
  (element.classList.contains('modal') || element.classList.contains('drawer'));

const InitModal = () => {
  if (!document.querySelector('dialog.modal, dialog.drawer')) {
    return;
  }

  // 글자를 고르다가 바탕에서 손을 떼면 click 대상이 dialog 가 되어 닫혀 버린다.
  // 누른 곳과 뗀 곳이 모두 바탕일 때만 닫도록 누른 곳을 기억해 둔다.
  let pressed = null;

  document.addEventListener('pointerdown', (event) => {
    pressed = event.target;
  });

  // 잠긴 모달은 Esc 로도 닫지 않는다. cancel 은 버블링하지 않아 캡처 단계에서 받는다.
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target instanceof HTMLDialogElement && isBusy(event.target)) {
        event.preventDefault();
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const opener = event.target.closest?.('[data-modal-open]');

    if (opener) {
      const dialog = document.getElementById(opener.dataset.modalOpen || '');

      if (dialog instanceof HTMLDialogElement && !dialog.open) {
        event.preventDefault();
        dialog.showModal();
      }

      return;
    }

    const closer = event.target.closest?.('[data-modal-close]');

    if (closer) {
      const dialog = closer.closest('dialog');

      if (!isBusy(dialog)) {
        dialog?.close();
      }

      return;
    }

    // 안쪽은 header · body · footer 가 틈 없이 채우므로 dialog 자신이 대상이면 바탕을 누른 것이다
    const target = event.target;

    if (isOverlay(target) && pressed === target && !isBusy(target)) {
      target.close();
    }
  });
};

export { lockModal };
export default InitModal;
